/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

/** @jsx jsx */

import React from 'react'
import { jsx } from '@emotion/react'
import { useIntl } from 'react-intl'

import { EuiButton, EuiButtonIcon, EuiFlexGroup, EuiFlexItem, EuiIcon, EuiText } from '@elastic/eui'

import type { FeedbackType } from './types'

interface PromptViewProps {
  feedbackSnippetId: string
  promptViewMessage: React.ReactNode
  onDismissPanel: () => void
  onFeedback: (feedback: FeedbackType) => void
}

const PromptView = ({
  feedbackSnippetId,
  promptViewMessage,
  onDismissPanel,
  onFeedback,
}: PromptViewProps) => {
  const { formatMessage } = useIntl()

  return (
    <EuiFlexGroup gutterSize='s' justifyContent='center' responsive={false} direction='column'>
      <EuiFlexGroup gutterSize='s' justifyContent='spaceBetween' responsive={false}>
        <EuiText size='s' textAlign='center'>
          {promptViewMessage}
        </EuiText>
        <EuiButtonIcon
          data-test-subj={`${feedbackSnippetId}_panel-dismiss`}
          iconType='cross'
          color='text'
          onClick={onDismissPanel}
          id={`${feedbackSnippetId}_panel-dismiss`}
          aria-label={formatMessage({
            id: 'feedbackSnippet.feedbackPanel.closePanel',
            defaultMessage: 'Close feedback panel',
          })}
        />
      </EuiFlexGroup>
      <EuiFlexGroup gutterSize='s' justifyContent='center' responsive={false}>
        <EuiFlexItem grow={false}>
          <EuiButton
            data-test-subj={`${feedbackSnippetId}_panel-thumb-down`}
            onClick={() => onFeedback('negative')}
            id={`${feedbackSnippetId}_panel-thumb-down`}
            color='danger'
            size='s'
          >
            <EuiIcon type='thumbDown' />
          </EuiButton>
        </EuiFlexItem>
        <EuiFlexItem grow={false}>
          <EuiButton
            data-test-subj={`${feedbackSnippetId}_panel-thumb-up`}
            onClick={() => onFeedback('positive')}
            id={`${feedbackSnippetId}_panel-thumb-up`}
            color='success'
            size='s'
          >
            <EuiIcon type='thumbUp' />
          </EuiButton>
        </EuiFlexItem>
      </EuiFlexGroup>
    </EuiFlexGroup>
  )
}

export default PromptView
